import { useCallback, useEffect, useRef, useState } from 'react';
import type { ApiClient } from '../../../utils/apiClient';
import { useApiMutation } from '../../../utils/useApiMutation';
import { useCursorList, type CursorPage } from '../../../utils/useCursorList';
import { BULK_DISMISS_OLDER_THAN_DAYS } from '../designTokens';
import type { DismissFilter, DismissResult, InboxItem, MarkReadResult } from '../types';

const INBOX_PAGE_SIZE = 20;

export interface UseInboxPanelResult {
  items: InboxItem[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  error: string | null;
  reload: () => Promise<void>;
  /**
   * Whether an item should render as read.
   *
   * NOT simply `readAt !== null`: an item that was unread when this open began
   * keeps its unread treatment until the panel is closed, even though the
   * server has already marked it (AC-13, AC-29).
   */
  isRead: (item: InboxItem) => boolean;
  markingAll: boolean;
  markError: string | null;
  markAllRead: () => Promise<void>;
  dismissing: boolean;
  dismissError: string | null;
  dismissOne: (id: string) => Promise<void>;
  /** Returns how many rows the server actually cleared (AC-50). */
  clearRead: () => Promise<number>;
  clearOlderThanAMonth: () => Promise<number>;
}

/**
 * The bell panel's own list, and every action it exposes (IAN-26..IAN-28,
 * IAN-53).
 *
 * Mounting this hook IS "the panel opening": `NotificationBell` only mounts the
 * panel's subtree while the popover/sheet is shown, so a fresh call here always
 * means a fresh open, and unmounting always means a close. That is what lets
 * "read-on-open marks only what was rendered" (AC-13) be a plain one-shot
 * effect rather than something keyed on a boolean prop.
 *
 * Composed from {@link useCursorList} (the exact `{items, nextCursor}` shape
 * this endpoint returns) rather than a second hand-rolled accumulator.
 */
export function useInboxPanel(onUnreadChange?: (unread: number) => void): UseInboxPanelResult {
  const fetchPage = useCallback(
    (client: ApiClient, cursor: string | null) =>
      client.get<CursorPage<InboxItem>>(
        `/notifications/inbox?status=all&pageSize=${INBOX_PAGE_SIZE}${
          cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''
        }`,
      ),
    [],
  );

  const list = useCursorList<InboxItem>(fetchPage, [], {
    errorPrefix: 'Failed to load your notifications',
  });

  const { run: runMark, loading: markingAll, error: markError } = useApiMutation();
  const { run: runDismiss, loading: dismissing, error: dismissError } = useApiMutation();

  // Ids that were UNREAD at the moment this open began.
  //
  // They ARE marked read on the server straight away — the badge clears, which
  // is what AC-13 asks for — but they keep their unread treatment for the life
  // of this open. Deriving the treatment from the server's answer instead made
  // every accent and bold title vanish about a tenth of a second after the
  // panel appeared, so the distinction AC-29 requires existed only in a frame
  // nobody sees. What a reader needs to know is "what is new SINCE I last
  // looked", and that question is answered once, at open.
  //
  // Never re-computed once set, so an item that arrives afterwards — via
  // `loadMore`, or the `reload()` a bulk dismiss triggers — is simply absent
  // from it and renders unread on its own `readAt` (the SPEC edge case "the
  // panel is open when new items arrive").
  const [unreadAtOpenIds, setUnreadAtOpenIds] = useState<ReadonlySet<string>>(new Set());
  // Items the reader has explicitly cleared with "Mark all read". That is a
  // deliberate act whose entire purpose is to drop the markers, so it overrides
  // the freeze above rather than being overridden by it.
  const [forcedReadIds, setForcedReadIds] = useState<ReadonlySet<string>>(new Set());
  const markedOnOpenRef = useRef(false);

  useEffect(() => {
    if (markedOnOpenRef.current || list.loading) return;
    markedOnOpenRef.current = true;
    // Captured from the data, not from the write's response: the freeze is a
    // fact about what the reader was shown, so it must not depend on whether
    // the network call succeeds.
    setUnreadAtOpenIds(
      new Set(list.items.filter((item) => item.readAt === null).map((item) => item.id)),
    );
    const ids = list.items.map((item) => item.id);
    if (ids.length === 0) return;
    void runMark(
      (client) => client.post<MarkReadResult>('/notifications/inbox/read', { ids }),
      'Failed to mark notifications read',
    )
      .then((result) => {
        onUnreadChange?.(result.unread);
      })
      .catch(() => {
        // The read-on-open mark is a side effect of viewing, not a user
        // action — a failure here is reported via `markError` for the panel
        // to show, but it must not block the list itself from being usable.
      });
    // `list.items` deliberately omitted: `markedOnOpenRef` already guarantees
    // this body runs at most once per mount, and re-running it whenever the
    // array's identity changes (e.g. after `loadMore`) is exactly the bug this
    // hook exists to avoid.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.loading, runMark, onUnreadChange]);

  const [dismissedIds, setDismissedIds] = useState<ReadonlySet<string>>(new Set());

  const isRead = useCallback(
    (item: InboxItem) =>
      forcedReadIds.has(item.id) ||
      (item.readAt !== null && !unreadAtOpenIds.has(item.id)),
    [forcedReadIds, unreadAtOpenIds],
  );

  const markAllRead = useCallback(async () => {
    const result = await runMark(
      (client) => client.post<MarkReadResult>('/notifications/inbox/read', { all: true }),
      'Failed to mark every notification read',
    );
    // Every item currently rendered settles as read; anything beyond the
    // loaded pages is exactly what "Mark all read" is for; it does not need a
    // second round trip here to know it worked (SPEC "Edge cases").
    setForcedReadIds(new Set(list.items.map((item) => item.id)));
    onUnreadChange?.(result.unread);
  }, [runMark, list.items, onUnreadChange]);

  const dismissOne = useCallback(
    async (id: string) => {
      const result = await runDismiss(
        (client) => client.post<DismissResult>('/notifications/inbox/dismiss', { ids: [id] }),
        'Failed to dismiss this notification',
      );
      // Settled on the server's own response rather than removed optimistically
      // first — the same "no optimistic update" convention as the Settings
      // preferences toggle, so a failed dismiss leaves the item exactly where
      // it was rather than flickering it away and back.
      setDismissedIds((prev) => new Set(prev).add(id));
      onUnreadChange?.(result.unread);
    },
    [runDismiss, onUnreadChange],
  );

  const clearByFilter = useCallback(
    async (filter: DismissFilter) => {
      const result = await runDismiss(
        (client) => client.post<DismissResult>('/notifications/inbox/dismiss', filter),
        'Failed to clear these notifications',
      );
      onUnreadChange?.(result.unread);
      // A filtered clear can remove rows this session never loaded (older
      // pages it never scrolled to), so the honest way to reflect it is a
      // fresh page one rather than guessing which loaded rows matched.
      await list.reload();
      return result.dismissed;
    },
    [runDismiss, onUnreadChange, list],
  );

  const clearRead = useCallback(() => clearByFilter({ onlyRead: true }), [clearByFilter]);

  const clearOlderThanAMonth = useCallback(() => {
    const cutoff = new Date();
    cutoff.setUTCDate(cutoff.getUTCDate() - BULK_DISMISS_OLDER_THAN_DAYS);
    return clearByFilter({ olderThan: cutoff.toISOString() });
  }, [clearByFilter]);

  const items = list.items.filter((item) => !dismissedIds.has(item.id));

  return {
    items,
    loading: list.loading,
    loadingMore: list.loadingMore,
    hasMore: list.hasMore,
    loadMore: list.loadMore,
    error: list.error,
    reload: list.reload,
    isRead,
    markingAll,
    markError,
    markAllRead,
    dismissing,
    dismissError,
    dismissOne,
    clearRead,
    clearOlderThanAMonth,
  };
}
